/**
 * Paywall + payment helpers used across the frontend.
 *
 * - useConfig()    — fetches /api/config once (cached in module-level state)
 *                    and returns { paywall_active, brief_pricing_usd, ... }.
 * - useAccess(id)  — fetches /api/payments/access/{id} for the current assessment
 *                    and lets callers refetch on demand (e.g. after Stripe return).
 * - startCheckout({ assessment_id, tier }) — mints a checkout session and redirects.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

let _configCache = null;
let _configPromise = null;

export function fetchConfig() {
  if (_configCache) return Promise.resolve(_configCache);
  if (_configPromise) return _configPromise;
  _configPromise = api.get("/config").then(({ data }) => {
    if (data?.catalog_ok !== false) _configCache = data;
    _configPromise = null;
    return data;
  }).catch((e) => {
    _configPromise = null;
    throw e;
  });
  return _configPromise;
}

// Invalidate the cached /api/config (e.g. after an admin edits FAQs) so the next
// read reflects the change without a hard reload.
export function clearConfigCache() {
  _configCache = null;
  _configPromise = null;
}

export function useConfig() {
  const [config, setConfig] = useState(_configCache);
  const [loading, setLoading] = useState(!_configCache);
  useEffect(() => {
    let alive = true;
    if (!_configCache) {
      fetchConfig()
        .then((c) => { if (alive) { setConfig(c); setLoading(false); } })
        .catch(() => { if (alive) { setLoading(false); } });
    }
    return () => { alive = false; };
  }, []);
  return { config, loading };
}

export function useAccess(assessmentId) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(!!assessmentId);

  const refresh = useCallback(async () => {
    if (!assessmentId) return null;
    try {
      const { data } = await api.get(`/payments/access/${assessmentId}`);
      setAccess(data);
      return data;
    } catch (e) {
      console.warn("access fetch failed", e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { access, loading, refresh };
}

/** Mint a Stripe Checkout Session and navigate to it.
 *  Throws on failure (caller surfaces the toast). */
export async function startCheckout({ assessment_id, tier }) {
  const origin = window.location.origin;
  console.info("[checkout] requesting session", { assessment_id, tier, origin });
  let data;
  try {
    const resp = await api.post("/payments/checkout", { assessment_id, tier, origin_url: origin });
    data = resp.data;
  } catch (e) {
    console.error("[checkout] backend rejected request", e?.response?.status, e?.response?.data);
    throw new Error(e?.response?.data?.detail || "Backend rejected checkout request");
  }
  if (!data?.url) {
    console.error("[checkout] backend returned no url", data);
    throw new Error("No checkout URL returned");
  }
  console.info("[checkout] redirecting to Stripe", data.url);
  // Emergent Preview iframes the app. Stripe Checkout sets X-Frame-Options=DENY
  // (and a frame-ancestors CSP), so a plain `window.location.assign(url)` only
  // changes the IFRAME's location and Stripe refuses to render — the user sees
  // a blank page. We escape the iframe by navigating the TOP frame; if that's
  // cross-origin sandboxed we fall back to opening a new tab.
  const url = data.url;
  const isFramed = window.top && window.top !== window.self;
  if (isFramed) {
    try {
      window.top.location.href = url;
      return;
    } catch (e) {
      console.warn("[checkout] window.top.location blocked, opening new tab", e);
      const popup = window.open(url, "_blank", "noopener,noreferrer");
      if (popup) return;
      // Popup blocker — surface the URL so the caller can render a manual link.
      throw new Error(`Your browser blocked the redirect. Please open this URL manually: ${url}`);
    }
  }
  // Standalone — simplest, same-tab redirect.
  window.location.assign(url);
}

/** Poll /api/payments/status/{sid} until terminal or maxAttempts hit.
 *  Returns the final payload (or null on timeout). */
export async function pollPaymentStatus(sessionId, { onUpdate, maxAttempts = 6, intervalMs = 2000 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      onUpdate?.(data, attempt);
      if (["paid", "failed", "expired"].includes(data.status)) return data;
    } catch (e) {
      console.warn("status poll error", e);
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

// ---------------- Iteration 19 — LuniPay flat-fee unlock ----------------

/** Open the LuniPay product page in a new tab. Returns the popup ref (or null
 *  if the popup was blocked). Caller renders an "I've paid — unlock my plan"
 *  button so the user can claim their unlock when they're back in the app. */
export function openLunipay(url) {
  if (!url) {
    console.error("[lunipay] missing url");
    return null;
  }
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (!popup) {
    console.warn("[lunipay] popup blocked — user must allow popups");
  }
  return popup;
}

// ---------------- PayPal one-off checkout (Orders API v2) ----------------

/** Create a server-side PayPal order. Returns the PayPal order id that the
 *  PayPal JS SDK `createOrder` callback must resolve to.
 *  `product_slug` is "brief" (scan unlock) or "advisory" (consultation). */
export async function createPaypalOrder({ product_slug = "brief", resource_ref, email }) {
  const { data } = await api.post("/payments/order", {
    product_slug,
    provider: "paypal",
    resource_ref,
    email,
  });
  return data; // { provider, order_id, txn_id }
}

/** Capture an approved PayPal order. Called from the SDK `onApprove` callback.
 *  Marks the transaction paid server-side → paywall access auto-revalidates. */
export async function capturePaypalOrder(orderId) {
  const { data } = await api.post("/payments/paypal/capture", { order_id: orderId });
  return data; // { status }
}

/** Trust-based unlock — call AFTER the user has paid on LuniPay and clicked
 *  the "I've paid" button. Marks the assessment as paid; access auto-revalidates
 *  on the next /api/payments/access call.
 *  `tier` is "brief" (default — $27 watermark removal) or "advisory" (the
 *  $125 / 45-min consultation booking). */
export async function claimLunipay({ assessment_id, tier = "brief", note }) {
  const { data } = await api.post("/payments/lunipay/claim", { assessment_id, tier, note });
  return data;  // { status, tier, transaction_id, amount_usd, already_paid }
}
